import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { fmtNum } from 'TestDataManagement/tdmUtils';
import { ruleText, withObject, withoutObject, withRule, withChosenRule, withConfirmedRule, isDerived, objectIndex } from './dataGraphApi';
import { ruleStatText } from './ObjectReport';
import { KindFilterRow, withoutHiddenKinds } from './objectKinds';

// The object list of a LIST data graph (contract 3.0, 2026-11-03): the objects a subset saves, each with
// the rules that reach it — `Object.column IN (Source.column)`, Source being the key table or another
// listed object. A rule may keep at most N rows per source value; an object may be "use, don't save" (its
// rows feed rules, nothing of it is staged); an object with no rule is reference-only (integrity brings
// its rows). Suggestions come from the profile's links (getRuleSuggestions, ranked by the platform); a
// rule the profile has no link for is written by picking its two columns. Unsaved until Save settings.
// Since 3.1 (2026-11-04) an object with no rule gets one DERIVED by the planner at the Test (written back
// here with origin DERIVED): a default the Test's counts confirm or the user changes — Confirm keeps it as
// their own, the alternatives (the ranked suggestions) replace it with one click.

const SUGGESTIONS_SHOWN = 5;
const lower = (s) => String(s || '').toLowerCase();

// The columns of `name`, loaded once through `columnsOf` (the editor's cached getGraphNode).
const useColumns = (name, columnsOf) => {
  const [cols, setCols] = useState([]);
  useEffect(() => {
    let alive = true;
    setCols([]);
    if (!name || !columnsOf) return undefined;
    Promise.resolve(columnsOf(name)).then((c) => { if (alive) setCols(c || []); }).catch(() => {});
    return () => { alive = false; };
  }, [name, columnsOf]);
  return cols;
};

// A column: a select over the profiled columns, or free text while none are known.
const ColumnPick = ({ value, onChange, columns, disabled, testId, label }) => (columns.length > 0 ? (
  <select className='dg-input' value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testId} aria-label={label}>
    <option value=''>column…</option>
    {!columns.includes(value) && value && <option value={value}>{value}</option>}
    {columns.map((c) => <option key={c} value={c}>{c}</option>)}
  </select>
) : (
  <input className='dg-input' value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder='column'
    data-testid={testId} aria-label={label} />
));

// "Add a rule": this object's column IN a source's column. The source is the key table or another listed object.
const RuleForm = ({ object, sources, columnsOf, onAdd, onCancel, testId }) => {
  const [column, setColumn] = useState('');
  const [source, setSource] = useState(sources[0] || '');
  const [sourceColumn, setSourceColumn] = useState('');
  const ownColumns = useColumns(object, columnsOf);
  const sourceColumns = useColumns(source, columnsOf);
  const ready = column.trim() && source && sourceColumn.trim();
  return (
    <div className='dg-rule-form' data-testid={testId}>
      <span className='dg-mono'>{object}.</span>
      <ColumnPick value={column} onChange={setColumn} columns={ownColumns} testId={`${testId}-column`} label={`${object} column`} />
      <span>IN</span>
      <select className='dg-input' value={source} onChange={(e) => { setSource(e.target.value); setSourceColumn(''); }}
        data-testid={`${testId}-source`} aria-label='source object'>
        {sources.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <span>.</span>
      <ColumnPick value={sourceColumn} onChange={setSourceColumn} columns={sourceColumns} testId={`${testId}-source-column`} label='source column' />
      <Button size='sm' disabled={!ready} data-testid={`${testId}-add`}
        onClick={() => onAdd({ column: column.trim(), source, sourceColumn: sourceColumn.trim(), maxPerParent: null })}>Add rule</Button>
      <Button size='sm' variant='link' className='p-0' onClick={onCancel}>Cancel</Button>
    </div>
  );
};

const candidateRule = (c) => ({ column: c.column, source: c.source, sourceColumn: c.sourceColumn, maxPerParent: null, origin: 'GIVEN' });

const ObjectCard = ({
  object, keyTable, sources, columnsOf, disabled, counts, candidates, onUpdate, onRemove, testId,
}) => {
  const [adding, setAdding] = useState(false);
  const rules = object.rules || [];
  const isStart = lower(object.name) === lower(keyTable);
  const have = new Set(rules.map((r) => ruleText(object.name, r)));
  const fresh = (candidates || []).filter((c) => !have.has(ruleText(object.name, candidateRule(c)))).slice(0, SUGGESTIONS_SHOWN);
  const derived = rules.some(isDerived);
  const setRules = (next) => onUpdate({ ...object, rules: next });
  // A chosen candidate takes a derived rule's place; otherwise it is one more rule.
  const choose = (rule) => onUpdate(withChosenRule([object], object.name, rule)[0]);
  const setCap = (i, value) => {
    const v = String(value).trim();
    setRules(rules.map((r, j) => (j === i ? { ...r, maxPerParent: v === '' ? null : Number(v) } : r)));
  };
  return (
    <div className='dg-object' data-testid={testId}>
      <div className='dg-object__head'>
        <span className='dg-mono dg-object__name'>{object.name}</span>
        {isStart && <span className='dg-muted'>(key table: the selection, plus any rule below)</span>}
        <label className='dg-config__check m-0' title={'Its rows feed rules and are never staged, masked or provisioned'}>
          <input type='checkbox' checked={object.save === false} disabled={disabled || isStart}
            onChange={(e) => onUpdate({ ...object, save: !e.target.checked })} data-testid={`${testId}-nosave`} />
          use, don&apos;t save
        </label>
        {!disabled && (
          <Button size='sm' variant='link' className='p-0 ml-auto' data-testid={`${testId}-remove`} onClick={onRemove}>Remove</Button>
        )}
      </div>
      <div className='dg-object__rules'>
        {rules.length === 0 && !isStart && (
          <div className='dg-muted' data-testid={`${testId}-reference`}>
            No rule yet: the Test derives one from the profile&apos;s links (a configuration object, or one nothing links, comes by integrity alone).
          </div>
        )}
        {rules.map((r, i) => {
          const id = ruleText(object.name, r);
          const stat = counts && counts[id];
          return (
            <div className='dg-rule' key={id} data-testid={`${testId}-rule-${i}`}>
              <span className='dg-mono' title={id}>reached by {id}</span>
              {isDerived(r) && (
                <span className='dg-rule__origin' data-testid={`${testId}-rule-${i}-derived`}
                  title='The planner’s pick for an object that had no rule: a default. The Test’s counts say whether it is the right one; Confirm keeps it, an alternative below replaces it.'>
                  derived
                </span>
              )}
              {isDerived(r) && !disabled && (
                <Button size='sm' variant='link' className='p-0' data-testid={`${testId}-rule-${i}-confirm`}
                  onClick={() => onUpdate(withConfirmedRule([object], object.name, i)[0])}>Confirm</Button>
              )}
              <label className='m-0 dg-muted'>at most
                <input className='dg-input dg-input--cap ml-1' type='number' min='1' value={r.maxPerParent != null ? r.maxPerParent : ''}
                  placeholder='all' disabled={disabled} onChange={(e) => setCap(i, e.target.value)} data-testid={`${testId}-rule-${i}-cap`} />
                {' '}per parent
              </label>
              {stat && ruleStatText(stat) && <span className='dg-muted' data-testid={`${testId}-rule-${i}-counts`}>last Test: {ruleStatText(stat)}</span>}
              {!disabled && (
                <Button size='sm' variant='link' className='p-0' data-testid={`${testId}-rule-${i}-remove`}
                  onClick={() => setRules(rules.filter((_, j) => j !== i))}>Remove</Button>
              )}
            </div>
          );
        })}
      </div>
      {!disabled && fresh.length > 0 && (
        <div className='dg-suggest' data-testid={`${testId}-suggestions`}>
          <div className='dg-muted mb-1'>{derived ? 'Alternatives to the derived rule, from the profile’s links' : 'Suggested from the profile’s links'}</div>
          {fresh.map((c, i) => {
            const rule = candidateRule(c);
            return (
              <div className='dg-suggest__row' key={ruleText(object.name, rule)} data-testid={`${testId}-suggestion-${i}`}>
                <span className='dg-mono'>{ruleText(object.name, rule)}</span>
                {c.reason && <span className='dg-muted'>{c.reason}</span>}
                <Button size='sm' variant='outline-primary' data-testid={`${testId}-suggestion-${i}-use`}
                  onClick={() => choose(rule)}>{derived ? 'Use instead' : 'Use'}</Button>
              </div>
            );
          })}
        </div>
      )}
      {!disabled && (adding
        ? <RuleForm object={object.name} sources={sources} columnsOf={columnsOf} testId={`${testId}-ruleform`}
          onCancel={() => setAdding(false)}
          onAdd={(rule) => { onUpdate(withRule([object], object.name, { ...rule, origin: 'GIVEN' })[0]); setAdding(false); }} />
        : (
          <Button size='sm' variant='link' className='p-0 mt-1' disabled={sources.length === 0} data-testid={`${testId}-add-rule`}
            title={sources.length === 0 ? 'Set the key table first: a rule reads from it or from another listed object' : undefined}
            onClick={() => setAdding(true)}>+ Add a rule</Button>
        ))}
    </div>
  );
};

/**
 * `objects` / `onChange(next)`: the list. `tables`: the graph's nodes (for the add-object picker).
 * `columnsOf(name)`: a promise of the object's column names. `counts`: rule id → the last Test's counts.
 * `suggestions`: [{ object, candidates }] from getRuleSuggestions; `onSuggest` asks for them.
 */
const ObjectListEditor = ({
  objects, onChange, keyTable, tables, columnsOf, disabled, counts, suggestions, onSuggest, suggesting, suggestError,
}) => {
  const [adding, setAdding] = useState('');
  // Every profiled object is offered (type to search); the kind toggles narrow the suggestions.
  const [hiddenKinds, setHiddenKinds] = useState(() => new Set());
  const list = useMemo(() => objects || [], [objects]);
  const names = useMemo(() => withoutHiddenKinds(tables, hiddenKinds).map((n) => (n.schema ? `${n.schema}.${n.table}` : n.table)),
    [tables, hiddenKinds]);
  const byObject = useMemo(() => {
    const m = {};
    (suggestions || []).forEach((s) => { if (s && s.object) m[lower(s.object)] = s.candidates || []; });
    return m;
  }, [suggestions]);
  const sourcesFor = (name) => {
    const out = [];
    if (keyTable && lower(keyTable) !== lower(name)) out.push(keyTable);
    list.forEach((o) => { if (lower(o.name) !== lower(name) && !out.some((s) => lower(s) === lower(o.name))) out.push(o.name); });
    return out;
  };
  const add = () => {
    const n = adding.trim();
    if (!n) return;
    onChange(withObject(list, n));
    setAdding('');
  };
  const keyListed = keyTable && objectIndex(list, keyTable) >= 0;
  return (
    <div className='dg-objects' data-testid='dg-objects'>
      <div className='dg-section__title mb-0'>Objects · {fmtNum(list.length)}</div>
      {keyTable && !keyListed && (
        <div className='dg-muted' data-testid='dg-objects-start'>
          Starts at <span className='dg-mono'>{keyTable}</span>, saved with the selection.
          {!disabled && (
            <>
              {' '}
              <button type='button' className='dg-link' data-testid='dg-objects-list-start'
                onClick={() => onChange(withObject(list, keyTable))}>List it</button> to give it more rules.
            </>
          )}
        </div>
      )}
      {list.length === 0 && (
        <div className='dg-muted' data-testid='dg-objects-empty'>
          No objects listed yet: only the key table&apos;s selected rows would be saved. Tick the objects the subset should hold in the list below, or add one here.
        </div>
      )}
      {list.map((o, i) => (
        <ObjectCard key={lower(o.name)} object={o} keyTable={keyTable} sources={sourcesFor(o.name)} columnsOf={columnsOf}
          disabled={disabled} counts={counts} candidates={byObject[lower(o.name)]} testId={`dg-object-${o.name}`}
          onUpdate={(next) => onChange(list.map((x, j) => (j === i ? next : x)))}
          onRemove={() => onChange(withoutObject(list, o.name))} />
      ))}
      {!disabled && (
        <div className='dg-builder'>
          <input className='dg-input dg-input--wide' list='dg-objects-tables' value={adding} placeholder='Add an object…'
            onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            data-testid='dg-objects-add-name' aria-label='Object to add' />
          <datalist id='dg-objects-tables'>{names.map((n) => <option key={n} value={n} />)}</datalist>
          <Button size='sm' variant='outline-primary' disabled={!adding.trim()} onClick={add} data-testid='dg-objects-add'>Add</Button>
          {onSuggest && (
            <Button size='sm' variant='outline-secondary' disabled={suggesting || list.length === 0 || !keyTable} onClick={onSuggest}
              data-testid='dg-objects-suggest' title='Rules from the profile’s links between the listed objects and the key table, ranked'>
              {suggesting ? 'Suggesting…' : 'Suggest rules'}
            </Button>
          )}
        </div>
      )}
      {!disabled && (
        <KindFilterRow items={tables} hidden={hiddenKinds} onChange={setHiddenKinds} listName='Add an object' testId='dg-objects-kinds' />
      )}
      {suggestError && <div className='dg-error' data-testid='dg-objects-suggest-error'>{suggestError}</div>}
    </div>
  );
};

export default ObjectListEditor;
